import { useFormik, FormikProvider, Field, Form as FormikForm } from "formik"
import { Col, Button, Form } from "react-bootstrap"
import * as Yup from "yup"
import styles from "./AccountManagement.module.scss"
import { useState } from "react"
import { BeatLoader } from "react-spinners"
import { getAccountInfo } from "shared/routes/account/info"
import { useModel } from "@stem-sims/nexus"
import { StudentAccountCreateProps } from "shared/types/accountManagementTypes"
import { createStudentAccount } from "shared/routes/account/account-management"
import { toast } from "react-toastify"

const CreateStudentAccountPage = ({ refreshCallback } : { refreshCallback: () => void }) => {
    const [isSubmitting, setIsSubmitting] = useState(false)

    const { response: accountInfo, loading } = useModel({
        model: getAccountInfo
    })

    const handleSubmit = async (values: StudentAccountCreateProps) => {
        setIsSubmitting(true)
        createStudentAccount(values)
        .then(() => {
            toast.success("Student account created successfully.")
            refreshCallback()
        })
        .catch((err) => {
            toast.error(err.response?.data ?? "An error occurred while creating the student account.")
        })
        .finally(() => {
            setIsSubmitting(false)
            formik.resetForm()
        })
    }

    const formik = useFormik({
        initialValues: {
            username: accountInfo?.username ? `${accountInfo?.username} Student` : "",
            password: "",
            confirmPassword: "",
        },
        onSubmit: handleSubmit,
        enableReinitialize: true,
        validationSchema: Yup
            .object()
            .shape({
                username: Yup
                    .string()
                    .label("Student Username")
                    .required("Student Username is required."),
                password: Yup
                    .string()
                    .label("Student Password")
                    .min(5, "Student password must be 5 characters long")
                    .max(150)
                    .required("Student Password is required."),
                confirmPassword: Yup
                    .string()
                    .label("Confirm Student Password")
                    .min(5, "Confirm password must be 5 characters long")
                    .max(150)
                    .oneOf([Yup.ref("password"), null], "Passwords need to match")
                    .required("Confirm Student Password is required."),
            })
    })

    if (loading || isSubmitting) return <BeatLoader />

  return (
    <div className="mt-4 w-75 mx-auto">
        <h4>Create Student Account</h4>
        <div className="mt-3">
            <p className="text-muted text-start">
                Filling out this form will create a Student account that is separate from the account you are currently logged in with (your Teacher account), and 
                that will allow your students to access all content except for the teacher materials, which are only accessible by you through your Teacher account.
            </p>
            <p className="text-muted text-start">
                We <b>strongly</b> recommend that you do not use the same password for both the Teacher and Student accounts.
            </p>
            <p className="text-muted text-start">
                Please note that you will only create <b>one</b> student account, and distribute that login information to all of your students to use simultaneously.
            </p>
        </div>
        <div className="d-flex justify-content-center mt-5">
            <FormikProvider value={formik}>
                <Form as={FormikForm} className={`${styles.container} border border-1 border-secondary rounded p-3`}>
                    <Form.Group controlId="formCreateStudentUsername" className="my-4" as={Col}>
                        <Form.Label className={`${styles.label} d-block text-muted`}>Username</Form.Label>
                        <Form.Control
                            className={`${styles.input}`}
                            name="username"
                            type="text"
                            placeholder="Username"
                            as={Field}
                            onChange={formik.handleChange}
                            value={formik.values.username}
                            isInvalid={formik.touched.username && !!formik.errors.username}
                        />
                        <Form.Control.Feedback type="invalid" className="text-start">
                            {formik.errors.username}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="formCreateStudentPassword" className="my-4" as={Col}>
                        <Form.Label className={`${styles.label} d-block text-muted`}>Student Password</Form.Label>
                        <Form.Control
                            className={`${styles.input}`}
                            name="password"
                            type="password"
                            placeholder="Student Password"
                            as={Field}
                            onChange={formik.handleChange}
                            value={formik.values.password}
                            isInvalid={formik.touched.password && !!formik.errors.password}
                        />
                        <Form.Control.Feedback type="invalid" className="text-start">
                            {formik.errors.password}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="formCreateConfirmPassword" className="my-4" as={Col}>
                        <Form.Label className={`${styles.label} d-block text-muted`}>Confirm Student Password</Form.Label>
                        <Form.Control
                            className={`${styles.input}`}
                            name="confirmPassword"
                            type="password"
                            placeholder="Confirm Student Password"
                            as={Field}
                            onChange={formik.handleChange}
                            value={formik.values.confirmPassword}
                            isInvalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
                        />
                        <Form.Control.Feedback type="invalid" className="text-start">
                            {formik.errors.confirmPassword}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Button
                        className="rounded-lg px-4 py-2 mt-4"
                        variant="theme"
                        disabled={!formik.dirty || !formik.isValid || isSubmitting}
                        type="submit">
                            Create Student Account
                    </Button>
                </Form>
            </FormikProvider>
        </div>
    </div>
  )
}

export default CreateStudentAccountPage
